import { useQuery } from '@tanstack/react-query';
import { catalogService } from '../../shared/services';
import type { Curso } from '../../shared/types';
import { PlayCircle, CheckCircle, Clock, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

export function MyContentPage() {
  const { data: cursosData } = useQuery({
    queryKey: ['cursos-meus'],
    queryFn: () => catalogService.getCursos({ page: 1 }),
  });

  // A API retorna array direto, não objeto
  const cursos = Array.isArray(cursosData) ? cursosData : [];

  // Filtra cursos em andamento e concluídos
  const cursosEmAndamento = cursos.filter(
    (c: Curso) => c.progresso && c.progresso.percentual > 0 && c.progresso.percentual < 100
  );

  const cursosConcluidos = cursos.filter(
    (c: Curso) => c.progresso && c.progresso.percentual === 100
  );

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Meus Conteúdos</h1>
          <p className="text-gray-600 mt-2">Acompanhe seu progresso nos cursos</p>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Em Andamento</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {cursosEmAndamento.length}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <PlayCircle className="text-yellow-600" size={24} />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Concluídos</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {cursosConcluidos.length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Horas</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {Math.floor((cursos.reduce((acc: number, c: Curso) => acc + (c.cargaHorariaMin || 0), 0) || 0) / 60)}h
                </p>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Clock className="text-primary-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Cursos em andamento */}
        {cursosEmAndamento.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Em Andamento</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {cursosEmAndamento.map((curso: Curso) => (
                <Link
                  key={curso.id}
                  to={`/cursos/${curso.slug}`}
                  className="card hover:shadow-lg transition-shadow group"
                >
                  <div className="flex gap-4">
                    <div className="w-32 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {curso.capaUrl ? (
                        <img
                          src={curso.capaUrl}
                          alt={curso.titulo}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen size={32} className="text-gray-400" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded">
                        {curso.categoria}
                      </span>

                      <h3 className="font-semibold text-gray-900 mt-2 mb-3 group-hover:text-primary-600 transition-colors">
                        {curso.titulo}
                      </h3>

                      {curso.progresso && (
                        <div>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-600">
                              {curso.progresso.aulasConcluidas} de {curso.progresso.totalAulas} aulas
                            </span>
                            <span className="font-semibold text-gray-900">
                              {curso.progresso.percentual}%
                            </span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary-600 transition-all"
                              style={{ width: `${curso.progresso.percentual}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Cursos concluídos */}
        {cursosConcluidos.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Concluídos</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {cursosConcluidos.map((curso: Curso) => (
                <Link
                  key={curso.id}
                  to={`/cursos/${curso.slug}`}
                  className="card hover:shadow-lg transition-shadow group"
                >
                  <div className="relative w-full aspect-video bg-gray-100 rounded-lg overflow-hidden mb-4">
                    {curso.capaUrl ? (
                      <img
                        src={curso.capaUrl}
                        alt={curso.titulo}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen size={48} className="text-gray-400" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="text-white" size={20} />
                    </div>
                  </div>

                  <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded">
                    {curso.categoria}
                  </span>

                  <h3 className="font-semibold text-gray-900 mt-2 group-hover:text-primary-600 transition-colors">
                    {curso.titulo}
                  </h3>

                  <p className="text-xs text-gray-500 mt-2">
                    Concluído • {curso.cargaHorariaMin} min
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {cursosEmAndamento.length === 0 && cursosConcluidos.length === 0 && (
          <div className="card text-center py-12">
            <BookOpen className="mx-auto text-gray-400 mb-4" size={64} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhum curso iniciado
            </h3>
            <p className="text-gray-600 mb-6">
              Explore o catálogo e comece sua jornada de aprendizado
            </p>
            <Link to="/catalogo" className="btn-primary inline-block">
              Explorar Cursos
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
